import { Tabs, TabsList, TabsTrigger } from "@/components/primitives/tabs";

import { ScrollArea, ScrollBar } from "@/components/primitives/scroll-area";
import ReactionsList from "./ReactionsList";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

type Props = {
  chatId: string;
  messageId: string;
  reactionSummary: [string, number][];
};

export default function ReactionsContent({
  chatId,
  messageId,
  reactionSummary,
}: Props) {
  const [activeTab, setActiveTab] = useState("all");

  // check reaction summary and if there are no reactions on a specific active tab, set active tab to all
  useEffect(() => {
    if (activeTab !== "all") {
      const hasReactionsInTab = reactionSummary.some(
        ([emoji]) => emoji === activeTab,
      );
      if (!hasReactionsInTab) {
        setActiveTab("all");
      }
    }
  }, [reactionSummary, activeTab]);

  return (
    <Tabs
      defaultValue="all"
      value={activeTab}
      onValueChange={setActiveTab}
      className="flex size-full flex-col overflow-hidden"
    >
      <ScrollArea className="w-full whitespace-nowrap p-0">
        <TabsList className="mb-3 h-16 bg-transparent">
          <div className="flex w-max py-2 text-lg">
            <TabsTrigger
              key="all"
              value="all"
              className="flex h-12 w-20 items-center gap-1 rounded-l-md rounded-r-none data-[state=active]:bg-primary lg:hover:bg-primary"
            >
              All
            </TabsTrigger>
            {reactionSummary.map(([emoji, count], index) => (
              <TabsTrigger
                key={emoji}
                value={emoji}
                className={cn(
                  "flex h-12 w-20 items-center gap-1 rounded-none data-[state=active]:bg-primary lg:hover:bg-primary",
                  index === reactionSummary.length - 1 && "rounded-r-md",
                )}
              >
                <span className="">{emoji}</span>
                <span className="font-semibold">{count}</span>
              </TabsTrigger>
            ))}
          </div>

          <ScrollBar orientation="horizontal" />
        </TabsList>
      </ScrollArea>

      {activeTab === "all" && (
        <ReactionsList
          chatId={chatId}
          messageId={messageId}
          emoji={undefined}
          tabValue="all"
        />
      )}

      {reactionSummary.map(
        ([emoji]) =>
          activeTab === emoji && (
            <ReactionsList
              key={emoji}
              chatId={chatId}
              messageId={messageId}
              emoji={emoji}
              tabValue={emoji}
            />
          ),
      )}
    </Tabs>
  );
}
